import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingState } from "@/components/ui/loading-state";
import { logger } from "@/lib/logger";

interface AuthGuardProps {
  children: React.ReactNode;
  requireOnboarding?: boolean;
  requiredRole?: string;
}

export const AuthGuard = ({ children, requireOnboarding = true, requiredRole }: AuthGuardProps) => {
  const [checking, setChecking] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    const checkAuth = async () => {
      logger.info('AuthGuard checking authentication', {
        pathname: location.pathname,
        authLoading,
        hasUser: !!user,
        requireOnboarding,
        requiredRole
      });

      // Wait for auth to finish loading
      if (authLoading) {
        logger.debug('AuthGuard waiting for auth to finish loading');
        return;
      }
      
      if (!user) {
        logger.warn('AuthGuard: No user found, redirecting to login', {
          pathname: location.pathname
        });
        navigate('/login');
        return;
      }

      logger.info('AuthGuard: User authenticated', {
        userId: user.id,
        email: user.email
      });

      // Verificar se completou onboarding
      if (requireOnboarding && location.pathname !== '/onboarding') {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('id', user.id)
          .single();

        if (profileError) {
          logger.error('AuthGuard: Failed to fetch profile', profileError);
        }

        logger.debug('AuthGuard: Profile check', {
          hasProfile: !!profile,
          onboardingCompleted: profile?.onboarding_completed
        });

        if (!profile?.onboarding_completed) {
          logger.info('AuthGuard: Onboarding not completed, redirecting', {
            userId: user.id
          });
          navigate('/onboarding');
          return;
        }
      }

      // Verificar role se necessário
      if (requiredRole) {
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', requiredRole as 'user' | 'mentor' | 'admin' | 'moderator')
          .single();

        if (roleError) {
          logger.error('AuthGuard: Failed to fetch role', roleError);
        }

        logger.debug('AuthGuard: Role check', {
          requiredRole,
          hasRole: !!roleData
        });

        if (!roleData) {
          logger.warn('AuthGuard: User does not have required role, redirecting to home', {
            userId: user.id,
            requiredRole
          });
          navigate('/');
          return;
        }
      }

      logger.info('AuthGuard: All checks passed, allowing access', {
        pathname: location.pathname,
        userId: user.id
      });

      setAuthenticated(true);
      setChecking(false);
    };

    checkAuth();
  }, [user, authLoading, navigate, location.pathname, requireOnboarding, requiredRole]);

  if (authLoading || checking) {
    return <LoadingState variant="spinner" fullScreen />;
  }

  return authenticated ? <>{children}</> : null;
};
